import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/ui/AdminSidebar';
import AdminDownbar from '../../components/ui/AdminDownbar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import DashboardService from '../../services/dashboardService';
import AdminService from '../../services/adminService';
import Image from '../../components/AppImage';

const formatDateTime = (date) =>
  new Date(date).toLocaleString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

const ensureArray = (arr) => Array.isArray(arr) ? arr : [];

const AdminDashboardScreen = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [lithayMetrics, setLithayMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check admin authentication
    if (!AdminService.isAuthenticated()) {
      navigate('/login-screen');
      return;
    }

    let isMounted = true;
    const fetchLithayMetrics = async () => {
      setLoading(true);
      try {
        const stats = await DashboardService.getDashboardStats();
        if (isMounted) setLithayMetrics(stats);
      } catch (error) {
        console.error('Error al cargar métricas:', error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchLithayMetrics();
    return () => { isMounted = false };
  }, [navigate]);

  const handleCategoryClick = () => navigate('/admin-categories');
  const handleAuthorClick = () => navigate('/admin-authors');

  // Función para truncar texto a 10 caracteres
  const truncateText = (text, maxLength = 10) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Desktop: 3 autores, Mobile: 2 categorías + 2 autores
  const visibleCategoriesDesktop = ensureArray(lithayMetrics?.categoryStats).slice(0, 4);
  const extraCategoriesDesktop = ensureArray(lithayMetrics?.categoryStats).length - visibleCategoriesDesktop.length;

  const visibleCategoriesMobile = ensureArray(lithayMetrics?.categoryStats).slice(0, 2);
  const extraCategoriesMobile = ensureArray(lithayMetrics?.categoryStats).length - visibleCategoriesMobile.length;

  const visibleAuthorsDesktop = ensureArray(lithayMetrics?.authorStats).slice(0, 3);
  const extraAuthorsDesktop = ensureArray(lithayMetrics?.authorStats).length - visibleAuthorsDesktop.length;

  const visibleAuthorsMobile = ensureArray(lithayMetrics?.authorStats).slice(0, 2);
  const extraAuthorsMobile = ensureArray(lithayMetrics?.authorStats).length - visibleAuthorsMobile.length;

  return (
    <>
      <Helmet>
        <title>Panel de Administración - Librería Lithay</title>
        <meta name="description" content="Panel de control administrativo para gestión de catálogo, autores y categorías en Librería Lithay" />
      </Helmet>

      <div className="min-h-screen bg-slate-50 pb-[140px] lg:pb-0">
        {/* Desktop Sidebar */}
        <div className="hidden lg:block">
          <AdminSidebar
            isCollapsed={sidebarCollapsed}
            onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
            userName="Administrador"
          />
        </div>
        {/* Mobile Bottom Bar */}
        <div className="lg:hidden">
          <AdminDownbar />
        </div>
        <main className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
          {/* Encabezado */}
          <header className="sticky top-0 z-20 p-6 bg-white/80 border-b border-slate-200 backdrop-blur-lg">
            <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-[#c37344]/10">
                    <Icon name="LayoutDashboard" size={24} className="text-[#c37344]" />
                  </div>
                  <h1 className="text-xl sm:text-2xl font-heading font-bold text-[#0c3454] whitespace-nowrap">
                    Panel de Administración
                  </h1>
                </div>
                <p className="mt-2 text-sm italic text-[#624859]">
                  “Cada uno según el don que ha recibido, minístrelo a los otros, como buenos administradores de la multiforme gracia de Dios.” — 1 Pedro 4:10
                </p>
                <p className="mt-1 text-xs text-[#687e90]">{formatDateTime(new Date())}</p>
              </div>
            </div>
          </header>

          {/* Contenido principal */}
          <div className="p-6">
            <div className="max-w-7xl mx-auto space-y-6">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="flex items-center space-x-3">
                    <Icon name="Loader" size={24} className="animate-spin text-[#c37344]" />
                    <span className="text-[#624859]">Cargando estadísticas...</span>
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Categorías */}
                    <div className="w-full p-6 bg-white border rounded-xl shadow-md border-slate-200">
                      <h3 className="mb-4 text-lg font-bold text-[#0c3454]">Categorías</h3>
                      <div className="flex flex-wrap gap-3">
                        {/* Desktop: 4 categorías, Mobile: 3 categorías */}
                        <div className="hidden lg:flex flex-wrap gap-3">
                          {visibleCategoriesDesktop.map((cat) => (
                            <div
                              key={cat.id || cat.name}
                              onClick={handleCategoryClick}
                              className="px-3 py-1.5 text-sm transition-colors duration-200 ease-in-out bg-[#687e90]/10 text-[#624859] rounded-full cursor-pointer hover:bg-[#687e90]/20 hover:text-[#0c3454]"
                              title={cat.name}
                            >
                              <span className="truncate">{truncateText(cat.name, 10)}</span>
                            </div>
                          ))}
                          {extraCategoriesDesktop > 0 && (
                            <div
                              onClick={handleCategoryClick}
                              className="px-3 py-1.5 text-sm font-semibold transition-colors duration-200 ease-in-out bg-[#c37344]/20 text-[#c37344] rounded-full cursor-pointer hover:bg-[#c37344]/30"
                              title="Ver todas las categorías"
                            >
                              +{extraCategoriesDesktop}
                            </div>
                          )}
                        </div>
                        <div className="flex lg:hidden flex-wrap gap-3">
                          {visibleCategoriesMobile.map((cat) => (
                            <div
                              key={cat.id || cat.name}
                              onClick={handleCategoryClick}
                              className="px-3 py-1.5 text-sm transition-colors duration-200 ease-in-out bg-[#687e90]/10 text-[#624859] rounded-full cursor-pointer hover:bg-[#687e90]/20 hover:text-[#0c3454]"
                              title={cat.name}
                            >
                              <span className="truncate">{truncateText(cat.name, 10)}</span>
                            </div>
                          ))}
                          {extraCategoriesMobile > 0 && (
                            <div
                              onClick={handleCategoryClick}
                              className="px-3 py-1.5 text-sm font-semibold transition-colors duration-200 ease-in-out bg-[#c37344]/20 text-[#c37344] rounded-full cursor-pointer hover:bg-[#c37344]/30"
                              title="Ver todas las categorías"
                            >
                              +{extraCategoriesMobile}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Autores */}
                    <div className="w-full p-6 bg-white border rounded-xl shadow-md border-slate-200">
                      <h3 className="mb-4 text-lg font-bold text-[#0c3454]">Autores</h3>
                      <div className="flex flex-wrap gap-3">
                        {/* Desktop: 3 autores, Mobile: 2 autores */}
                        <div className="hidden lg:flex flex-wrap gap-3">
                          {visibleAuthorsDesktop.map((author) => (
                            <div
                              key={author.id || author.author}
                              onClick={handleAuthorClick}
                              className="px-3 py-1.5 text-sm transition-colors duration-200 ease-in-out bg-[#687e90]/10 text-[#624859] rounded-full cursor-pointer hover:bg-[#687e90]/20 hover:text-[#0c3454]"
                              title={author.author}
                            >
                              <span className="truncate">{truncateText(author.author, 10)}</span>
                            </div>
                          ))}
                          {extraAuthorsDesktop > 0 && (
                            <div
                              onClick={handleAuthorClick}
                              className="px-3 py-1.5 text-sm font-semibold transition-colors duration-200 ease-in-out bg-[#c37344]/20 text-[#c37344] rounded-full cursor-pointer hover:bg-[#c37344]/30"
                              title="Ver todos los autores"
                            >
                              +{extraAuthorsDesktop}
                            </div>
                          )}
                        </div>
                        <div className="flex lg:hidden flex-wrap gap-3">
                          {visibleAuthorsMobile.map((author) => (
                            <div
                              key={author.id || author.author}
                              onClick={handleAuthorClick}
                              className="px-3 py-1.5 text-sm transition-colors duration-200 ease-in-out bg-[#687e90]/10 text-[#624859] rounded-full cursor-pointer hover:bg-[#687e90]/20 hover:text-[#0c3454]"
                              title={author.author}
                            >
                              <span className="truncate">{truncateText(author.author, 10)}</span>
                            </div>
                          ))}
                          {extraAuthorsMobile > 0 && (
                            <div
                              onClick={handleAuthorClick}
                              className="px-3 py-1.5 text-sm font-semibold transition-colors duration-200 ease-in-out bg-[#c37344]/20 text-[#c37344] rounded-full cursor-pointer hover:bg-[#c37344]/30"
                              title="Ver todos los autores"
                            >
                              +{extraAuthorsMobile}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Productos recientes */}
                  {ensureArray(lithayMetrics?.recentBooks).length > 0 ? (
                    <div className="space-y-4">
                      <h2 className="text-xl font-bold text-[#0c3454]">Productos Recientes</h2>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {ensureArray(lithayMetrics.recentBooks)
                          .sort((a, b) => b.id_product - a.id_product)
                          .slice(0, 10)
                          .map((product) => (
                            <li
                              key={product.id_product}
                              className="flex items-center p-4 space-x-4 transition-all duration-200 bg-white border rounded-xl shadow-md border-slate-200 hover:shadow-lg hover:border-[#c37344]"
                            >
                              <div className="flex-shrink-0 w-16 h-20 overflow-hidden border rounded-md bg-slate-100 border-slate-200">
                                {product.image_url_product?.[0] ? (
                                  <Image
                                    src={product.image_url_product[0]}
                                    alt={product.title_product}
                                    className="object-cover w-full h-full"
                                    low
                                  />
                                ) : (
                                  <div className="flex items-center justify-center h-full text-xs italic text-[#687e90]">
                                    Sin imagen
                                  </div>
                                )}
                              </div>
                              <div className="flex flex-col justify-center">
                                <p className="font-semibold leading-tight text-[#0c3454]">
                                  {product.title_product || 'Sin título'}
                                </p>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                                    product.format_product && product.language_product
                                      ? 'bg-green-100 text-green-700' // Biblia
                                      : product.is_book_product === true || product.pages_product
                                      ? 'bg-blue-100 text-blue-700' // Libro
                                      : 'bg-purple-100 text-purple-700' // Thing
                                  }`}>
                                    {product.format_product && product.language_product
                                      ? 'Biblia'
                                      : product.is_book_product === true || product.pages_product
                                      ? 'Libro'
                                      : product.type_thing || 'Sin tipo'
                                    }
                                  </span>
                                  {(product.format_product && product.language_product) || (product.is_book_product === true || product.pages_product) ? (
                                    <p className="text-sm text-[#687e90]">
                                      {product.language_product || 'Sin especificar'}
                                    </p>
                                  ) : null}
                                </div>
                              </div>
                            </li>
                          ))}
                      </ul>
                    </div>
                  ) : (
                    <p className="italic text-[#624859]">No hay productos registrados recientemente.</p>
                  )}
                </>
              )}
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default AdminDashboardScreen;
                               
